import React, { useState, Fragment, useEffect, useMemo } from 'react';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';

import { DEFAULT_REPORT_TITLE, NEW_UTM_KICKOFF_DATE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { CustomUTMEquipmentSummary, CustomUTMOverview, EquipmentAnomalySummaryItem, NewAnomalyItem, PDFImageProps, PIDData, UTMInspection, User } from '../../../utils/models';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomExxonMobilUTMEquipmentReport from './customExxonMobilUTMEquipmentReport';
import CustomAnomalyItems from '../custom-general/custom-anomaly-items';
import CustomUTMEquipmentSummaryOverviewForm from '../../forms/custom-utm/customUtmEquipmentSummaryOverviewForm';
import CustomUTMSection from './custom-utm-section';
import Elevation from '../../themed/instruction-modals/elevation';
import EquipmentAnomalySummary from '../../forms/custom-fvi/equipmentAnomalySummary';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import FVICoverData from '../../themed/fvi-cover-data';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import MUTPhotograpicRecord from '../../forms/mut/mutPhotographicRecord';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import VIGeneralDrawingForm from '../../forms/visual-inspection/viGeneralDrawingForm';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';

import { getCurrentUser } from '../../../services/authService';
import { getEquipmentAnomalyClassOptions  } from '../../../utils/data';
import { getIsSameOrAfter, matchPhrases, reArrangeAnomalies, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { EQUIPMENT_LOCATION_OPTIONS } from '../custom-visual-inspection/existing-equipment-item';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useEquipmentAnomalySummaryUpdate from '../../../hooks/useEquipmentAnomalySummaryUpdate';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';
import useAnomalyRecords from '../../../hooks/useAnomalyRecords';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CreateCustomExxonMobilUTMEquipmentReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [summary, setSummary] = useState<CustomUTMEquipmentSummary>(initialEquipmentSummary);
	const [anomalyItems, setAnomalyItems] = useState<NewAnomalyItem[]>([]);
	const [anomalySummary, setAnomalySummary] = useState(initialAnomalySummary);
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [generalDrawing, setGeneralDrawing] = useState<PDFImageProps[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [isoImage, setIsoImage] = useState<PDFImageProps[]>([]);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			reportTitle,
			coverData,
			summary,
			anomalyItems,
			anomalySummary,
			pidData,
			generalDrawing,
			photographicData,
			projectDetails,
			isoImage,
			inspectionOverview: utInspectionOverview,
			utInspectionItems,
		};
	}, [anomalyItems, anomalySummary, projectDetails, coverData, isoImage, pidData, generalDrawing, summary, photographicData, reportTitle, utInspectionOverview, utInspectionItems]);

	const inspectionItemReadingsMissing = useMemo(() => {
		if (utInspectionItems.length === 0) return true;

		return utInspectionItems.some((item) => !item.sectionTitle || item.items.length === 0);
	}, [utInspectionItems]);

	const isNewUTMTemplate = useMemo(() => {
		if (!projectDetails?.createdAt) return false;

		return getIsSameOrAfter(projectDetails.createdAt, NEW_UTM_KICKOFF_DATE)
	}, [projectDetails?.createdAt]);

	const validationMessage = useMemo(() => {
		if (!reportDetails.anomalySummary.supportCondition) {
			return 'Provide a valid support condition';
		}

		if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber))
			return 'Enter a valid work order number!';

		if (reportDetails.summary.equipmentNumber.length === 0) {
			return 'Please complete summary information!';
		}

		if (matchPhrases(reportDetails.pidData.hasPID, 'yes') && reportDetails.pidData.pIDImages.length === 0) {
			return 'P&ID images missing!';
		}

		if (reportDetails.anomalySummary.pitting.length === 0) {
			return 'Please provide a value for pitting!';
		}

		if (reportDetails.anomalySummary.extentOfCoatingBreakdown.length === 0) {
			return 'Please provide a value for Coating Breakdown!';
		}
		
		if (reportDetails.anomalySummary.description.length === 0) {
			return 'Please provide a value for anomaly description!';
		}

		if (reportDetails.anomalySummary.others.length === 0) {
			return 'Please provide a value for anomaly summary (others)!';
		}

		if (matchPhrases(reportDetails.summary.hasAnomalies, 'yes')) {
			if (reportDetails.anomalyItems.length === 0) {
				return 'Please upload at least one anomaly!.'
			}
		}
		
		if (reportDetails.photographicData.length < 2) {
			return 'Please provide at least 2 photos!';
		}

		if (reportDetails.isoImage.length === 0) {
			return 'Please upload Isometric drawing!';
		}

		if (reportDetails.inspectionOverview.equipment.length === 0) {
			return 'Please complete the inspection overview!';
		}

		if (inspectionItemReadingsMissing) {
			return 'Please complete the inspection items section!';
		}
			
		return '';
	}, [reportDetails, inspectionItemReadingsMissing])

	const togglePreviewReport = (): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const dispatch = useAppDispatch();
	const autoSave = useAutoSaveReport(reportDetails);

	useAnomalyRecords(anomalyItems);
	useInspectedItemIDUpdate([summary.equipmentNumber]);
	
	useEquipmentAnomalySummaryUpdate({
		isNewFormat: isNewUTMTemplate,
		anomalyItems, 
		anomalySummary,
		onSaveAnomalySummary: setAnomalySummary,
	});

	useEffect(() => {
		function initializeCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

				return null;
			}

			setReportTitle(cachedReport.reportTitle);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setAnomalyItems(reArrangeAnomalies(cachedReport.anomalyItems));
			setAnomalySummary(cachedReport.anomalySummary);
			setSummary(cachedReport.summary);
			setPIDData(cachedReport.pidData);
			setGeneralDrawing(cachedReport.generalDrawing ?? []);
			setUTInspectionOverview(cachedReport.inspectionOverview);
			setUTInspectionItems(cachedReport.utInspectionItems);
			setIsoImage(cachedReport.isoImage);

			const photoRecord = reArrangePhotoRecord(cachedReport.photographicData);
			setPhotographicData(photoRecord);
			dispatch(setLastPhotoNumber(photoRecord.length));
		}

		initializeCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.UTM));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomExxonMobilUTMEquipmentReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilUTMEquipment[projectDetails.organisationId]}
					isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilUTMEquipment && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} UTM Equipment Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title='Incomplete Report' body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
			<Zippy headerTitle="Summary" isValid={summary.equipmentNumber.length > 0}>
				<CustomUTMEquipmentSummaryOverviewForm initialSummary={summary} onSave={setSummary} />
			</Zippy>
			
			<Zippy headerTitle='Anomaly Summary & Description' isValid={anomalySummary.pitting.length > 0}>
				<EquipmentAnomalySummary 
					hasAnomalies={summary.hasAnomalies} 
					initialAnomalySummary={anomalySummary} 
					onAdd={setAnomalySummary} 
					useNewFormat={isNewUTMTemplate}
				/>
			</Zippy>

			<Conditional isVisible={matchPhrases(summary.hasAnomalies, 'yes')}>
				<Zippy headerTitle="Anomaly Items" isValid={anomalyItems.length > 0}>
					<CustomAnomalyItems
						isPDOFormat={false}
						nextAnomalyID={anomalyItems.length + 1}
						locationOptions={EQUIPMENT_LOCATION_OPTIONS}
						isNewFormat={isNewUTMTemplate}
						damageClasses={getEquipmentAnomalyClassOptions()}
						items={anomalyItems}
						onDataChange={(items) => setAnomalyItems(items)}
					/>
				</Zippy>
			</Conditional>

			<Zippy headerTitle="Photographic Report" isValid={photographicData.length > 0}>
				<MUTPhotograpicRecord initialImages={photographicData} onProceed={setPhotographicData} />
			</Zippy>

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Zippy headerTitle="General Drawing (Optional)" isValid={true}>
				<VIGeneralDrawingForm initialGeneralDrawing={generalDrawing} onProceed={setGeneralDrawing} />
			</Zippy>

			<CustomUTMSection
				tmlLocations={isoImage}
				onChangeTMLLocations={setIsoImage}
				utmItems={utInspectionItems}
				utmOverview={utInspectionOverview}
				onItemsChange={setUTInspectionItems}
				onOverviewChange={setUTInspectionOverview}
			/>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
	techniqueType: '',
	velocity: 0,
};

const initialAnomalySummary: EquipmentAnomalySummaryItem = {
	pipeBody: '',
	noOfPoints: 0,
	pitting: '',
	noOfPittingLocations: 0,
	flanges: '',
	fasteners: '',
	valves: '',
	corrosionUnderPipeSupport: '',
	noOfCups: 0,
	supportCondition: '',
	compositeWrapOnLine: 'no',
	others: '',
	description: '',
	extentOfCoatingBreakdown: '',
	
	pipe: '',
	guage: '',
	dish: '',
	topDish: '',
	bottomDish: '',
	nozzle: '',
	namePlate: '',
	skirt: '',
	structure: '',
	externalProtection: '',
	insulation: '',
	
	noOfPipe: 0,
	noOfGuage: 0,
	noOfDish: 0,
	noOfTopDish: 0,
	noOfBottomDish: 0,
	noOfNozzle: 0,
	noOfNamePlate: 0,
	noOfSkirt: 0,
	noOfStructure: 0,
	noOfExternalProtection: 0,
	noOfInsulation: 0,
};

const initialEquipmentSummary: CustomUTMEquipmentSummary = { 
	hasAnomalies: 'no', 
	equipmentNumber: '', 
	overview: ''
};

export default CreateCustomExxonMobilUTMEquipmentReport;
